/* =====================================================================
   HOMEPAGE TEXT — English (en) and Korean (ko).
   Keep the same structure in both languages.

   Formatting inside strings (see <Rich> in src/site/ui/helpers.jsx):
     [[words]]   highlighted in mint (use rarely)
     blank line  ("\n\n") a deliberate line break — avoid in headings
     single "\n" is treated as a space; let the browser wrap text naturally.

   `media` values are slot names from src/site/media.js.
   Flow status values: "available" | "demo" | "planned".
   ===================================================================== */

export const home = {
  en: {
    hero: {
      filmTitle: "From Signal to Intelligence",
      filmMeta: "Brand film · 2 min",
      pause: "Pause slideshow",
      play: "Play slideshow",
      label: "Highlights",
      slides: [
        {
          media: "hero.1",
          eyebrow: "KS-101 hardware · K SMART V2 software",
          title: "From Signal [[to Intelligence]]",
          lead: "K SMART SYSTEMS connects sewing machines, operators and orders on the garment floor, so supervisors, managers and brands work from the same production data.",
          cta: { label: "Try the digital twin", href: "#twin" },
          cta2: { label: "Talk to KSS", to: "/contact" },
          tab: { key: "01 · Signals", text: "From the machine to the data" },
        },
        {
          media: "hero.2",
          eyebrow: "Capture and validate",
          title: "Every stitch, read against [[the work standard.]]",
          lead: "A count alone is not intelligence. KS-101 reads each stitch, trim and stop, and K SMART V2 checks it against the style's work standard before it counts.",
          cta: { label: "See how it works", href: "#flow" },
          tab: { key: "02 · Standards", text: "Counted only when it's right" },
        },
        {
          media: "hero.3",
          eyebrow: "See and improve",
          title: "Find the bottleneck [[before the shift ends.]]",
          lead: "Station-level data shows where work piles up. Try moving operators in the simulated digital twin and watch the finish time change.",
          cta: { label: "Play with the digital twin", href: "#twin" },
          tab: { key: "03 · Balance", text: "Test a fix before you make it" },
        },
      ],
    },

    statement: {
      eyebrow: "Why KSS",
      lead: "Every factory already speaks in signals: a stitch, a trim, a stop.",
      dim: "Most of them are lost between the machine and the morning report.",
      before: "KSS turns them into",
      mark: "one production picture",
      after: " that everyone on the line can act on.", // starts with a space on purpose
      body: "A sewing floor runs on thousands of short operations, frequent style changes and people at every step. Most of what happens there is never measured until the end of the hour or the day. KSS records it as it happens, at the machine.",
      signature: "Make every stitch count.",
      viz: {
        label: "Line 3 · live signals",
        lanes: ["STITCH", "TRIM", "STOP"],
        hub: "K SMART V2",
        picture: "Production picture",
        axis: "Stations 1–5",
        stats: ["Signals counted", "Bundles done", "Signals lost"],
        note: "Animated illustration with sample values.",
        pause: "Pause animation", play: "Play animation",
      },
    },

    model: {
      eyebrow: "Inside the platform",
      title: "Four layers. One connected factory.",
      intro: "Watch a signal leave the sewing machine, get validated, and reach the people who need it. Drag the model to turn it.",
      hint: "Drag to rotate · illustration",
      signal: "Signal",
      canvasLabel: "Animated model of four stacked platform layers, with signals rising from sewing machines to role views",
      // order: top → bottom (the list shows Intelligence first)
      layers: [
        { id: 3, tag: "Intelligence", title: "Plan the next move", body: "Line balancing, simulation and forecasts built on validated history. In development." },
        { id: 2, tag: "Applications", title: "See it in your role", body: "K SMART V2 views for operators, managers and brands, with MES and ERP links." },
        { id: 1, tag: "Data", title: "Keep every record true", body: "K SMART V2 checks signals against the work standard and stores one consistent record." },
        { id: 0, tag: "Physical", title: "Where information starts", body: "Sewing machines fitted with KS-101 hardware, and the operators on the line." },
      ],
      canvasText: { layers: ["PHYSICAL · KS-101", "DATA · K SMART V2", "APPLICATIONS", "INTELLIGENCE"], planned: "PLANNED", apps: ["OPERATOR", "MANAGER", "BRAND"], counter: "SIGNALS" },
    },

    flow: {
      eyebrow: "How it works",
      title: "From a stitch to a better-balanced line.",
      lead: "Nine steps, each building on the one before. Scroll through them. The first five are what KSS installs today; the rest are shown in the simulation below or are in development.",
      legend: { available: "Available now", demo: "Shown in the simulation", planned: "In development" },
      groups: [
        {
          media: "flow.floor", alt: "A long garment sewing floor with rows of machines",
          name: "On the floor", caption: "Steps 1–3 · on the floor",
          stops: [
            { title: "Factory operations", body: "Operators, machines, bundles and orders on the sewing line.", status: "available" },
            { title: "Sewing machine signals", body: "Every stitch, thread trim and stop leaves an electrical signal at the machine.", status: "available" },
            { title: "Data collection", body: "KS-101 hardware reads those signals. Operators log in and confirm each bundle at the station terminal.", status: "available", product: "KS-101" },
          ],
        },
        {
          media: "flow.data", alt: "A production planner reviewing line dashboards on two monitors",
          name: "In K SMART V2", caption: "Steps 4–5 · in K SMART V2",
          stops: [
            { title: "Structured production data", body: "Signals are checked against the style's work standard and stored with line, operation, order and time.", status: "available", product: "K SMART V2" },
            { title: "Dashboard visibility", body: "Operators, supervisors and brands each see the same validated numbers in their own view.", status: "available", product: "K SMART V2" },
          ],
        },
        {
          media: "flow.improve", alt: "Engineers and supervisors discussing a production plan at a control cabinet",
          name: "Improving the line", caption: "Steps 6–9 · improving the line",
          stops: [
            { title: "Bottleneck identification", body: "Station-level output shows which operation is holding the whole line back.", status: "demo" },
            { title: "Line balancing", body: "Compare moving operators between stations before changing anything on the floor.", status: "demo" },
            { title: "Production targets", body: "Estimate whether today's target will be met, and when, from the line's actual rate.", status: "demo" },
            { title: "AI-assisted optimisation", body: "Suggestions learned from a factory's own validated history.", status: "planned" },
          ],
        },
      ],
    },

    views: {
      eyebrow: "Who uses KSS",
      title: "Three people. One production picture.",
      intro: "Not a fixed dashboard. Each role sees the part of the line it can act on, built from the same validated data.",
      note: "Photos are illustrative. Screens use sample data.",
      link: "Open the role views demo",
      roles: [
        { media: "roles.operator", tag: "Operators", title: "Know the target for every bundle", body: "The station terminal shows the operation, the current bundle and output against the hourly target, in the operator's language.", chips: ["Login and size at the station", "Target vs actual", "No paper tally sheets"] },
        { media: "roles.manager", tag: "Supervisors and managers", title: "See where the line is slowing down", body: "Line output, station status and work waiting between operations, while there is still time in the shift to act on it.", chips: ["Output by station", "Stopped and waiting", "Work piling up"] },
        { media: "roles.brand", tag: "Brands and buyers", title: "Follow an order by what's been made", body: "Order progress comes from completed bundles on the sewing line, not from a weekly status email.", chips: ["Progress by order", "Delivery outlook"] },
      ],
    },

    station: {
      eyebrow: "One bundle at a station",
      title: "Watch a bundle go from login to “done”.",
      body: "A simplified illustration of what happens at one sewing station: the needle runs, KS-101 reads every stitch and trim, and the terminal counts the bundle until K SMART V2 marks it complete.",
      note: "Animated illustration with sample values.",
      cta: "Plan a pilot line",
      machineLabel: "Sewing machine with KS-101",
      signalLabel: "Signal",
      ks: "KS-101",
      pause: "Pause animation",
      play: "Play animation",
      terminal: {
        aria: "Illustration of a KSS station terminal handling one bundle",
        station: "Station 12", online: "Online",
        operator: "Operator", operation: "Operation", style: "Style", size: "Size",
        operationValue: "Sleeve attach", operatorValue: "Op. 3-12", styleValue: "KS-2419 Oxford shirt", sizeValue: "M",
        of: "of 24 pcs", bundle: "Bundle B-2312-07", stitches: "stitches",
        states: [
          { msg: "Welcome back. [[Line 3, station 12.]]", btn: "Logged in" },
          { msg: "Style and size [[loaded from the order.]]", btn: "Start sewing" },
          { msg: "Counting stitches [[and trims.]]", btn: "Sewing…" },
          { msg: "Bundle complete. [[Checked and sent.]]", btn: "Next bundle" },
        ],
      },
      stepsLabel: "Bundle steps",
      steps: [
        { title: "Log in", body: "The operator taps in at the terminal." },
        { title: "Select size", body: "Style and size load from the order." },
        { title: "Start sewing", body: "KS-101 counts stitches and trims." },
        { title: "Bundle complete", body: "Checked and shared with every view." },
      ],
    },

    problems: {
      eyebrow: "What changes",
      title: "Problems it helps with.",
      lead: "Every factory is different, so a pilot starts by agreeing which of these matters most on one line.",
      head: ["On many lines today", "With KSS"],
      rows: [
        ["Output is counted by hand at the end of the hour or the day.", "Completed bundles are recorded at the machine as they happen."],
        ["The slow operation is found after the line has already fallen behind.", "Station status and work waiting between operations show where the line is held up."],
        ["Operators are moved on experience alone.", "Balancing options can be compared using the line's own numbers. Recommendations are in development."],
        ["Brands ask for status and wait for a spreadsheet.", "Order progress comes from what the line has actually made."],
      ],
    },

    path: {
      title: "Start with one line. Grow with confidence.",
      intro: "Each rollout is configured around your styles, work standards and existing systems, then proven on a single line before it grows.",
      link: "How a deployment works",
      steps: [
        { tag: "Step 1", title: "Discover", body: "Walk the floor together and map operations, machines and the decisions you want to improve." },
        { tag: "Step 2 · Pilot", title: "Connect", body: "Fit KS-101 hardware and terminals on one line and load its styles and work standards." },
        { tag: "Step 3 · Pilot review", title: "Validate", body: "Compare recorded counts with real output, together with your supervisors." },
        { tag: "Step 4 · Rollout", title: "Expand", body: "Add lines, sites and modules once the data is trusted." },
      ],
    },

    explore: {
      title: "More to explore",
      prev: "Previous", next: "Next",
      cards: [
        { media: "explore.film", tag: "Film · 2 min", title: "From Signal to Intelligence", body: "The KSS story, from a single stitch to an AI operating layer.", go: "Watch the film", action: "film" },
        { media: "explore.twin", tag: "Digital twin", title: "Find the bottleneck yourself", body: "Three sample lines. Spot the slowest station, move an operator, see the finish time change.", go: "Open the digital twin", to: "/demo#twin" },
        { media: "explore.roles", tag: "Role views", title: "Try the three role screens", body: "Operator, factory manager and brand screens on sample garment data.", go: "Open the demo", to: "/demo#platform-demo" },
        { media: "explore.pilot", tag: "Pilot programme", title: "What a one-line pilot looks like", body: "Scope, steps and what your team gets at the end of it.", go: "See the projects", to: "/projects" },
      ],
    },

    signoff: {
      eyebrow: "Start a conversation",
      title: "Make every [[stitch]] count.",
      body: "Tell us about one line: its styles, its targets and where it usually falls behind. We'll reply with where its data could start.",
      cta: "Contact KSS",
      copy: "Copy", copied: "Copied",
    },
  },

  /* ----------------------------- 한국어 ----------------------------- */
  ko: {
    hero: {
      filmTitle: "신호에서 인텔리전스로",
      filmMeta: "브랜드 필름 · 2분",
      pause: "슬라이드 일시정지",
      play: "슬라이드 재생",
      label: "주요 내용",
      slides: [
        {
          media: "hero.1",
          eyebrow: "KS-101 하드웨어 · K SMART V2 소프트웨어",
          title: "신호에서 [[인텔리전스로]]",
          lead: "K SMART SYSTEMS는 봉제 현장의 재봉기, 작업자, 오더를 연결해 반장, 관리자, 브랜드가 같은 생산 데이터로 일하도록 돕습니다.",
          cta: { label: "디지털 트윈 체험하기", href: "#twin" },
          cta2: { label: "KSS에 문의하기", to: "/contact" },
          tab: { key: "01 · 신호", text: "재봉기에서 데이터까지" },
        },
        {
          media: "hero.2",
          eyebrow: "수집과 검증",
          title: "모든 스티치를 [[작업 표준으로 읽습니다.]]",
          lead: "숫자만으로는 인텔리전스가 아닙니다. KS-101이 스티치, 트림, 정지를 읽고, K SMART V2가 스타일의 작업 표준과 대조한 뒤에 실적으로 집계합니다.",
          cta: { label: "작동 방식 보기", href: "#flow" },
          tab: { key: "02 · 표준", text: "맞을 때만 집계" },
        },
        {
          media: "hero.3",
          eyebrow: "확인과 개선",
          title: "근무가 끝나기 전에 [[병목을 찾습니다.]]",
          lead: "공정 단위 데이터로 일이 쌓이는 곳을 보여줍니다. 시뮬레이션 디지털 트윈에서 작업자를 옮겨 보고 완료 시각이 어떻게 바뀌는지 확인하세요.",
          cta: { label: "디지털 트윈 해 보기", href: "#twin" },
          tab: { key: "03 · 밸런스", text: "바꾸기 전에 먼저 시험" },
        },
      ],
    },

    statement: {
      eyebrow: "왜 KSS인가",
      lead: "모든 공장은 이미 신호로 말하고 있습니다. 스티치, 트림, 정지.",
      dim: "그러나 대부분은 재봉기와 아침 보고서 사이에서 사라집니다.",
      before: "KSS는 이 신호를",
      mark: "하나의 생산 현황",
      after: "으로 바꿔 라인의 모든 사람이 바로 활용하게 합니다.",
      body: "봉제 현장은 수천 개의 짧은 공정, 잦은 스타일 변경, 그리고 모든 단계의 사람으로 돌아갑니다. 그 대부분은 한 시간이나 하루가 끝나야 집계됩니다. KSS는 재봉기에서, 일이 일어나는 순간 기록합니다.",
      signature: "Make every stitch count.",
      viz: {
        label: "3라인 · 실시간 신호",
        lanes: ["스티치", "트림", "정지"],
        hub: "K SMART V2",
        picture: "생산 현황",
        axis: "공정 1–5",
        stats: ["집계된 신호", "완료 번들", "놓친 신호"],
        note: "샘플 값을 사용한 애니메이션 예시입니다.",
        pause: "애니메이션 일시정지", play: "애니메이션 재생",
      },
    },

    model: {
      eyebrow: "플랫폼 구조",
      title: "네 개의 레이어, 하나로 연결된 공장.",
      intro: "신호가 재봉기를 떠나 검증을 거쳐 필요한 사람에게 도달하는 과정을 확인하세요. 모델을 드래그하면 돌려볼 수 있습니다.",
      hint: "드래그하여 회전 · 예시",
      signal: "신호",
      canvasLabel: "재봉기에서 역할별 화면까지 신호가 올라가는 4단계 플랫폼 레이어 애니메이션",
      layers: [
        { id: 3, tag: "인텔리전스", title: "다음 결정을 계획", body: "검증된 이력을 바탕으로 한 라인 밸런싱, 시뮬레이션, 예측. 개발 중." },
        { id: 2, tag: "애플리케이션", title: "역할에 맞는 화면", body: "작업자, 관리자, 브랜드를 위한 K SMART V2 화면과 MES·ERP 연동." },
        { id: 1, tag: "데이터", title: "모든 기록을 정확하게", body: "K SMART V2가 신호를 작업 표준과 대조해 일관된 하나의 기록으로 저장." },
        { id: 0, tag: "현장", title: "정보가 시작되는 곳", body: "KS-101 하드웨어가 부착된 재봉기와 라인의 작업자." },
      ],
      canvasText: { layers: ["현장 · KS-101", "데이터 · K SMART V2", "애플리케이션", "인텔리전스"], planned: "예정", apps: ["작업자", "관리자", "브랜드"], counter: "신호" },
    },

    flow: {
      eyebrow: "작동 방식",
      title: "한 땀의 신호에서 균형 잡힌 라인까지.",
      lead: "아홉 단계가 앞 단계 위에 차례로 쌓입니다. 스크롤하며 살펴보세요. 앞의 다섯 단계는 KSS가 현재 설치하는 기능이고, 나머지는 아래 시뮬레이션에서 보여드리거나 개발 중입니다.",
      legend: { available: "현재 제공", demo: "시뮬레이션에서 확인", planned: "개발 중" },
      groups: [
        {
          media: "flow.floor", alt: "재봉기가 줄지어 있는 넓은 봉제 현장",
          name: "현장에서", caption: "1–3단계 · 현장에서",
          stops: [
            { title: "공장 운영", body: "봉제 라인의 작업자, 재봉기, 번들, 오더.", status: "available" },
            { title: "재봉기 신호", body: "모든 스티치, 실 끊김(트림), 정지는 재봉기에 전기 신호로 남습니다.", status: "available" },
            { title: "데이터 수집", body: "KS-101 하드웨어가 이 신호를 읽고, 작업자는 공정 단말에서 로그인하고 번들을 확인합니다.", status: "available", product: "KS-101" },
          ],
        },
        {
          media: "flow.data", alt: "두 대의 모니터로 라인 대시보드를 검토하는 생산 담당자",
          name: "K SMART V2에서", caption: "4–5단계 · K SMART V2에서",
          stops: [
            { title: "구조화된 생산 데이터", body: "신호를 스타일의 작업 표준과 대조한 뒤 라인, 공정, 오더, 시간과 함께 저장합니다.", status: "available", product: "K SMART V2" },
            { title: "대시보드 가시성", body: "작업자, 반장, 브랜드가 같은 검증 데이터를 각자의 화면으로 봅니다.", status: "available", product: "K SMART V2" },
          ],
        },
        {
          media: "flow.improve", alt: "제어반 앞에서 생산 계획을 논의하는 엔지니어와 반장",
          name: "라인 개선", caption: "6–9단계 · 라인 개선",
          stops: [
            { title: "병목 파악", body: "공정별 생산량으로 어느 공정이 라인 전체를 붙잡고 있는지 보여줍니다.", status: "demo" },
            { title: "라인 밸런싱", body: "현장을 바꾸기 전에 작업자를 어느 공정으로 옮길지 비교합니다.", status: "demo" },
            { title: "생산 목표", body: "라인의 실제 속도로 오늘 목표를 달성할 수 있는지, 언제 달성하는지 예측합니다.", status: "demo" },
            { title: "AI 기반 최적화", body: "공장의 검증된 실제 이력으로 학습한 제안.", status: "planned" },
          ],
        },
      ],
    },

    views: {
      eyebrow: "KSS를 사용하는 사람",
      title: "세 사람, 하나의 생산 현황.",
      intro: "고정된 대시보드가 아닙니다. 같은 검증 데이터를 바탕으로 각 역할이 직접 조치할 수 있는 부분을 보여줍니다.",
      note: "사진은 예시이며, 화면은 샘플 데이터입니다.",
      link: "역할별 화면 데모 열기",
      roles: [
        { media: "roles.operator", tag: "작업자", title: "번들마다 목표를 확인합니다", body: "공정 단말에 작업, 현재 번들, 시간당 목표 대비 실적을 작업자의 언어로 보여줍니다.", chips: ["단말에서 로그인·사이즈 선택", "목표 대비 실적", "종이 집계표 없이"] },
        { media: "roles.manager", tag: "반장·관리자", title: "라인이 어디서 느려지는지 봅니다", body: "라인 생산량, 공정 상태, 공정 사이에 쌓인 재공을 근무 중 조치할 수 있을 때 보여줍니다.", chips: ["공정별 생산량", "정지·대기", "쌓이는 재공"] },
        { media: "roles.brand", tag: "브랜드·바이어", title: "실제 생산량으로 오더를 확인합니다", body: "오더 진행률은 주간 보고 메일이 아니라 봉제 라인에서 완료된 번들로 계산됩니다.", chips: ["오더별 진행률", "납기 전망"] },
      ],
    },

    station: {
      eyebrow: "공정 하나, 번들 하나",
      title: "로그인부터 ‘완료’까지, 번들 하나를 따라가 보세요.",
      body: "한 봉제 공정에서 일어나는 일을 단순화한 예시입니다. 바늘이 움직이면 KS-101이 모든 스티치와 트림을 읽고, 단말이 번들을 집계해 K SMART V2가 완료로 표시합니다.",
      note: "샘플 값을 사용한 애니메이션 예시입니다.",
      cta: "파일럿 라인 계획하기",
      machineLabel: "KS-101이 부착된 재봉기",
      signalLabel: "신호",
      ks: "KS-101",
      pause: "애니메이션 일시정지",
      play: "애니메이션 재생",
      terminal: {
        aria: "번들 하나를 처리하는 KSS 공정 단말 예시",
        station: "12번 공정", online: "온라인",
        operator: "작업자", operation: "공정", style: "스타일", size: "사이즈",
        operationValue: "소매 부착", operatorValue: "작업자 3-12", styleValue: "KS-2419 옥스퍼드 셔츠", sizeValue: "M",
        of: "/ 24장", bundle: "번들 B-2312-07", stitches: "스티치",
        states: [
          { msg: "다시 오신 것을 환영합니다. [[3라인 12번 공정.]]", btn: "로그인 완료" },
          { msg: "스타일과 사이즈를 [[오더에서 불러왔습니다.]]", btn: "재봉 시작" },
          { msg: "스티치와 트림을 [[집계하는 중입니다.]]", btn: "재봉 중…" },
          { msg: "번들 완료. [[검증 후 전송했습니다.]]", btn: "다음 번들" },
        ],
      },
      stepsLabel: "번들 처리 단계",
      steps: [
        { title: "로그인", body: "작업자가 단말에서 로그인합니다." },
        { title: "사이즈 선택", body: "오더의 스타일과 사이즈를 불러옵니다." },
        { title: "재봉 시작", body: "KS-101이 스티치와 트림을 집계합니다." },
        { title: "번들 완료", body: "검증 후 모든 화면에 공유합니다." },
      ],
    },

    problems: {
      eyebrow: "달라지는 점",
      title: "이런 문제를 돕습니다.",
      lead: "공장마다 사정이 다르기 때문에, 파일럿은 한 라인에서 무엇이 가장 중요한지 합의하는 것부터 시작합니다.",
      head: ["지금 많은 라인에서는", "KSS와 함께라면"],
      rows: [
        ["생산량을 한 시간이나 하루가 끝난 뒤 손으로 집계합니다.", "완료된 번들이 재봉기에서 바로 기록됩니다."],
        ["라인이 이미 뒤처진 뒤에야 느린 공정을 찾습니다.", "공정 상태와 공정 사이에 쌓인 재공으로 라인이 막힌 곳을 보여줍니다."],
        ["작업자 배치를 경험에만 의존해 바꿉니다.", "라인의 실제 수치로 밸런싱 방안을 비교할 수 있습니다. 추천 기능은 개발 중입니다."],
        ["브랜드가 현황을 묻고 스프레드시트를 기다립니다.", "오더 진행률이 라인의 실제 생산량에서 나옵니다."],
      ],
    },

    path: {
      title: "한 라인에서 시작해, 확신을 갖고 확장합니다.",
      intro: "각 도입은 고객의 스타일, 작업 표준, 기존 시스템에 맞춰 구성하고, 한 라인에서 먼저 검증한 뒤 확장합니다.",
      link: "도입 방식 자세히 보기",
      steps: [
        { tag: "1단계", title: "진단", body: "현장을 함께 보며 공정, 설비, 개선하고 싶은 의사결정을 정리합니다." },
        { tag: "2단계 · 파일럿", title: "연결", body: "한 라인에 KS-101 하드웨어와 단말을 설치하고 스타일과 작업 표준을 등록합니다." },
        { tag: "3단계 · 파일럿 검토", title: "검증", body: "반장과 함께 기록된 수량을 실제 생산량과 비교합니다." },
        { tag: "4단계 · 확산", title: "확장", body: "데이터가 신뢰를 얻으면 라인, 공장, 모듈을 넓혀갑니다." },
      ],
    },

    explore: {
      title: "더 알아보기",
      prev: "이전", next: "다음",
      cards: [
        { media: "explore.film", tag: "영상 · 2분", title: "신호에서 인텔리전스로", body: "하나의 스티치에서 AI 운영 레이어까지, KSS의 이야기.", go: "영상 보기", action: "film" },
        { media: "explore.twin", tag: "디지털 트윈", title: "직접 병목을 찾아보세요", body: "샘플 라인 세 개. 가장 느린 공정을 찾고 작업자를 옮겨 완료 시각의 변화를 확인하세요.", go: "디지털 트윈 열기", to: "/demo#twin" },
        { media: "explore.roles", tag: "역할별 화면", title: "세 가지 역할 화면 체험", body: "샘플 봉제 데이터로 보는 작업자, 공장 관리자, 브랜드 화면.", go: "데모 열기", to: "/demo#platform-demo" },
        { media: "explore.pilot", tag: "파일럿 프로그램", title: "한 라인 파일럿은 이렇게 진행됩니다", body: "범위, 단계, 그리고 마지막에 팀이 얻게 되는 것.", go: "프로젝트 보기", to: "/projects" },
      ],
    },

    signoff: {
      eyebrow: "상담 시작하기",
      title: "모든 [[한 땀]]이 데이터가 되도록.",
      body: "한 라인에 대해 알려주세요. 어떤 스타일을 만들고, 목표가 얼마이며, 주로 어디서 뒤처지는지 알려주시면 그 라인의 데이터를 어디서부터 시작할 수 있는지 답변드리겠습니다.",
      cta: "KSS에 문의하기",
      copy: "복사", copied: "복사됨",
    },
  },
};
